import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ApplyUpdateOnNavigate } from './components/AutoUpdate';
import { ProfileOnboardingModal } from './home/ProfileOnboardingModal';
import { isEmbedded } from './components/SplitView';
import { useAuth } from './auth/AuthProvider';
import { useProfile } from './profile/ProfileProvider';
import { Layout } from './components/Layout';
import { TodosProvider } from './notifications/TodosContext';
import { AppLockGate } from './applock/AppLockGate';
import {
  ExpensesSection,
  InvoicesSection,
  CreditCardsSection,
  CommissionsSection,
  SubscriptionsSection, AnalyticsSection, OperationsSection,
} from './components/sections';
import { RequireCommissionsAdmin } from './commissions/RequireCommissionsAdmin';
import { LoginPage } from './pages/LoginPage';
import { ContactHrPage } from './pages/ContactHrPage';
import { InactivePage } from './pages/InactivePage';
import { HubLandingPage } from './pages/HubLandingPage';
import { MorePage } from './pages/MorePage';
import { ActivityPage } from './pages/ActivityPage';
import { RequireInvoiceApprover } from './invoices/components/RequireInvoiceApprover';
import { RequireAdmin } from './routing/RequireAdmin';
import { RequireToolAccess } from './routing/RequireToolAccess';
import { AdminLayout } from './admin/AdminLayout';
import { AbsenceSection } from './absence/AbsenceSection';
import { DriversSection } from './drivers/DriversSection';
import { ConfigLayout } from './admin/config/ConfigLayout';
import { ConfigAccessProfilesLayout } from './admin/config/ConfigAccessProfilesLayout';
import { RequireMutationsAccess } from './mutations/access';
import { SUBSCRIPTION_NAV } from './subscriptions/registry';
import { SubscriptionPageGuard } from './subscriptions/SubscriptionPageGuard';
import { ANALYTICS_DASHBOARDS } from './analytics/registry';
import { AnalyticsPageGuard } from './analytics/AnalyticsPageGuard';
import { OPERATIONS_DASHBOARDS } from './operations/registry';
import { OperationsPageGuard } from './operations/OperationsPageGuard';
import { UsagePing } from './lib/usagePing';
import { ProjectsSection, RequireProjectsAdmin } from './projects/ProjectsSection';
import { directorsRoutes } from './directors/DirectorsRoutes';
import { Loading } from './components/Loading';
import { lazyPage } from './routing/lazyPage';

// Route-level code splitting (Oct 2026): every tool page below is its own
// chunk, loaded on first visit (precached by the service worker). Only the
// shell above (auth, Layout, nav, section tab shells, guards, Home) is eager.
// NEW PAGES GO HERE as lazyPage(), never as a static import — see
// routing/lazyPage.tsx.
const SelfProfilePage = lazyPage(() => import('./profile/SelfProfilePage'), 'SelfProfilePage');
const MyTeamPage = lazyPage(() => import('./myTeam/MyTeamPage'), 'MyTeamPage');
const GuidePage = lazyPage(() => import('./guide/GuidePage'), 'GuidePage');
const BoardPage = lazyPage(() => import('./board/BoardPage'), 'BoardPage');
const ShareReceiptPage = lazyPage(() => import('./creditCards/pages/ShareReceiptPage'), 'ShareReceiptPage');
const SignaturesPersonalPage = lazyPage(() => import('./signatures/SignaturesPage'), 'SignaturesPersonalPage');
const SignaturesTeamPage = lazyPage(() => import('./signatures/SignaturesPage'), 'SignaturesTeamPage');
const SignaturesTemplatesPage = lazyPage(() => import('./signatures/TemplatesPage'), 'SignaturesTemplatesPage');
const SignaturesStampsPage = lazyPage(() => import('./signatures/StampsPage'), 'SignaturesStampsPage');
const SignPage = lazyPage(() => import('./signatures/SignPage'), 'SignPage');
const MyOnboardingPage = lazyPage(() => import('./onboarding/MyOnboardingPage'), 'MyOnboardingPage');
const OnboardingChecklistPage = lazyPage(() => import('./onboarding/OnboardingChecklistPage'), 'OnboardingChecklistPage');
const AdminOnboardingPage = lazyPage(() => import('./admin/AdminOnboardingPage'), 'AdminOnboardingPage');
const VerifyPrivateEmailPage = lazyPage(() => import('./profile/VerifyPrivateEmailPage'), 'VerifyPrivateEmailPage');
const CreditCardDashboardPage = lazyPage(() => import('./creditCards/pages/CreditCardDashboardPage'), 'CreditCardDashboardPage');
const NewCreditCardStatementPage = lazyPage(() => import('./creditCards/pages/NewCreditCardStatementPage'), 'NewCreditCardStatementPage');
const CreditCardStatementDetailPage = lazyPage(() => import('./creditCards/pages/CreditCardStatementDetailPage'), 'CreditCardStatementDetailPage');
const ReceiptRequestPage = lazyPage(() => import('./creditCards/pages/ReceiptRequestPage'), 'ReceiptRequestPage');
const MyFeedbackPage = lazyPage(() => import('./feedback/MyFeedbackPage'), 'MyFeedbackPage');
const AdminFeedbackPage = lazyPage(() => import('./admin/AdminFeedbackPage'), 'AdminFeedbackPage');
const AdminAnnouncementsPage = lazyPage(() => import('./admin/AdminAnnouncementsPage'), 'AdminAnnouncementsPage');
const CreditCardReviewQueuePage = lazyPage(() => import('./creditCards/pages/CreditCardReviewQueuePage'), 'CreditCardReviewQueuePage');
const CreditCardLibraryPage = lazyPage(() => import('./creditCards/pages/CreditCardLibraryPage'), 'CreditCardLibraryPage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const CrmLinkPage = lazyPage(() => import('./crm/CrmLinkPage'), 'CrmLinkPage');
const TimeSummariesPage = lazyPage(() => import('./timeTracking/TimeSummariesPage'), 'TimeSummariesPage');
const ExpenseTeamPage = lazyPage(() => import('./pages/ExpenseTeamPage'), 'ExpenseTeamPage');
const StatementDetailPage = lazyPage(() => import('./pages/StatementDetailPage'), 'StatementDetailPage');
const AccountingDashboardPage = lazyPage(() => import('./pages/AccountingDashboardPage'), 'AccountingDashboardPage');
const AllowancesPage = lazyPage(() => import('./pages/AllowancesPage'), 'AllowancesPage');
const ResourcesPage = lazyPage(() => import('./pages/ResourcesPage'), 'ResourcesPage');
const InvoicesDashboardPage = lazyPage(() => import('./invoices/pages/InvoicesDashboardPage'), 'InvoicesDashboardPage');
const InvoiceFormPage = lazyPage(() => import('./invoices/pages/InvoiceFormPage'), 'InvoiceFormPage');
const InvoiceDetailPage = lazyPage(() => import('./invoices/pages/InvoiceDetailPage'), 'InvoiceDetailPage');
const InvoiceApprovalsPage = lazyPage(() => import('./invoices/pages/InvoiceApprovalsPage'), 'InvoiceApprovalsPage');
const InvoiceReviewPage = lazyPage(() => import('./invoices/pages/InvoiceReviewPage'), 'InvoiceReviewPage');
const AdminUsersPage = lazyPage(() => import('./admin/AdminUsersPage'), 'AdminUsersPage');
const AdminAccessPage = lazyPage(() => import('./admin/AdminAccessPage'), 'AdminAccessPage');
const UserProfilePage = lazyPage(() => import('./admin/UserProfilePage'), 'UserProfilePage');
const OrgChartPage = lazyPage(() => import('./orgChart/OrgChartPage'), 'OrgChartPage');
const OrgProfilePage = lazyPage(() => import('./orgChart/OrgProfilePage'), 'OrgProfilePage');
const DocumentsPage = lazyPage(() => import('./documents/DocumentsPage'), 'DocumentsPage');
const TeamDocumentsPage = lazyPage(() => import('./documents/TeamDocumentsPage'), 'TeamDocumentsPage');
const AdminDocumentVaultPage = lazyPage(() => import('./admin/AdminDocumentVaultPage'), 'AdminDocumentVaultPage');
const TimeTrackingPage = lazyPage(() => import('./timeTracking/TimeTrackingPage'), 'TimeTrackingPage');
const DriversCabinetPage = lazyPage(() => import('./drivers/pages/DriversCabinetPage'), 'DriversCabinetPage');
const DriversReceivedPage = lazyPage(() => import('./drivers/pages/DriversReceivedPage'), 'DriversReceivedPage');
const DriversGivenPage = lazyPage(() => import('./drivers/pages/DriversGivenPage'), 'DriversGivenPage');
const DriversBoardPage = lazyPage(() => import('./drivers/pages/DriversBoardPage'), 'DriversBoardPage');
const DriversTeamPage = lazyPage(() => import('./drivers/pages/DriversTeamPage'), 'DriversTeamPage');
const DriversOrgSummaryPage = lazyPage(() => import('./drivers/pages/DriversOrgSummaryPage'), 'DriversOrgSummaryPage');
const NewNominationPage = lazyPage(() => import('./drivers/pages/NewNominationPage'), 'NewNominationPage');
const AbsenceDashboardPage = lazyPage(() => import('./absence/pages/AbsenceDashboardPage'), 'AbsenceDashboardPage');
const AbsenceApprovalsPage = lazyPage(() => import('./absence/pages/AbsenceApprovalsPage'), 'AbsenceApprovalsPage');
const AbsenceCalendarPage = lazyPage(() => import('./absence/pages/AbsenceCalendarPage'), 'AbsenceCalendarPage');
const AbsenceSummariesPage = lazyPage(() => import('./absence/pages/AbsenceSummariesPage'), 'AbsenceSummariesPage');
const AbsenceMemberPage = lazyPage(() => import('./absence/pages/AbsenceMemberPage'), 'AbsenceMemberPage');
const AbsenceWarningsPage = lazyPage(() => import('./absence/pages/AbsenceWarningsPage'), 'AbsenceWarningsPage');
const AbsenceCarryOverPage = lazyPage(() => import('./absence/pages/AbsenceCarryOverPage'), 'AbsenceCarryOverPage');
const RemoteOverviewPage = lazyPage(() => import('./admin/remote/RemoteOverviewPage'), 'RemoteOverviewPage');
const RemoteExpensesPage = lazyPage(() => import('./admin/remote/RemoteExpensesPage'), 'RemoteExpensesPage');
const RemoteDocumentsPage = lazyPage(() => import('./admin/remote/RemoteDocumentsPage'), 'RemoteDocumentsPage');
const AdminCommandCenterFeedPage = lazyPage(() => import('./admin/AdminCommandCenterFeedPage'), 'AdminCommandCenterFeedPage');
const AdminRemoteLayout = lazyPage(() => import('./admin/remote/AdminRemoteLayout'), 'AdminRemoteLayout');
const AdminCountriesPage = lazyPage(() => import('./admin/AdminCountriesPage'), 'AdminCountriesPage');
const AdminResourcesPage = lazyPage(() => import('./admin/AdminResourcesPage'), 'AdminResourcesPage');
const AdminMailPage = lazyPage(() => import('./admin/AdminMailPage'), 'AdminMailPage');
const AdminEmailPreviewsPage = lazyPage(() => import('./admin/AdminEmailPreviewsPage'), 'AdminEmailPreviewsPage');
const AdminDataCompletenessPage = lazyPage(() => import('./admin/AdminDataCompletenessPage'), 'AdminDataCompletenessPage');
const ConfigLandingPage = lazyPage(() => import('./admin/config/ConfigLandingPage'), 'ConfigLandingPage');
const ConfigHubPage = lazyPage(() => import('./admin/config/ConfigHubPage'), 'ConfigHubPage');
const ConfigExpensePage = lazyPage(() => import('./admin/config/ConfigExpensePage'), 'ConfigExpensePage');
const ConfigInvoicePage = lazyPage(() => import('./admin/config/ConfigInvoicePage'), 'ConfigInvoicePage');
const ConfigCreditCardPage = lazyPage(() => import('./admin/config/ConfigCreditCardPage'), 'ConfigCreditCardPage');
const ConfigAbsencePage = lazyPage(() => import('./admin/config/ConfigAbsencePage'), 'ConfigAbsencePage');
const ConfigHolidaysPage = lazyPage(() => import('./admin/config/ConfigHolidaysPage'), 'ConfigHolidaysPage');
const ConfigAccessPage = lazyPage(() => import('./admin/config/ConfigAccessPage'), 'ConfigAccessPage');
const ConfigProfileFieldsPage = lazyPage(() => import('./admin/config/ConfigProfileFieldsPage'), 'ConfigProfileFieldsPage');
const ConfigDocumentVaultPage = lazyPage(() => import('./admin/config/ConfigDocumentVaultPage'), 'ConfigDocumentVaultPage');
const ConfigOnboardingPage = lazyPage(() => import('./admin/config/ConfigOnboardingPage'), 'ConfigOnboardingPage');
const ConfigDriversPage = lazyPage(() => import('./admin/config/ConfigDriversPage'), 'ConfigDriversPage');
const ConfigMutationsPage = lazyPage(() => import('./admin/config/ConfigMutationsPage'), 'ConfigMutationsPage');
const MutationsPage = lazyPage(() => import('./mutations/MutationsPage'), 'MutationsPage');
const ConfigCommissionsPage = lazyPage(() => import('./admin/config/ConfigCommissionsPage'), 'ConfigCommissionsPage');
const UslDashboardPage = lazyPage(() => import('./usl/UslDashboardPage'), 'UslDashboardPage');
const UslConfigPage = lazyPage(() => import('./usl/UslConfigPage'), 'UslConfigPage');
const CommissionsLandingPage = lazyPage(() => import('./commissions/CommissionsLandingPage'), 'CommissionsLandingPage');
const CommissionsPayoutPage = lazyPage(() => import('./commissions/CommissionsPayoutPage'), 'CommissionsPayoutPage');
const CommissionsAccrualPage = lazyPage(() => import('./commissions/CommissionsAccrualPage'), 'CommissionsAccrualPage');
const CommissionsStatementPage = lazyPage(() => import('./commissions/CommissionsStatementPage'), 'CommissionsStatementPage');
const CommissionsAgentsPage = lazyPage(() => import('./commissions/CommissionsAgentsPage'), 'CommissionsAgentsPage');
const CommissionsAgentsConfigPage = lazyPage(() => import('./commissions/CommissionsAgentsConfigPage'), 'CommissionsAgentsConfigPage');
const CommissionsAgentStatementPage = lazyPage(() => import('./commissions/CommissionsAgentStatementPage'), 'CommissionsAgentStatementPage');
const CommissionsAgentPayoutPage = lazyPage(() => import('./commissions/CommissionsAgentPayoutPage'), 'CommissionsAgentPayoutPage');
const CommissionsOtherPage = lazyPage(() => import('./commissions/CommissionsOtherPage'), 'CommissionsOtherPage');
const CommissionsOtherStatementPage = lazyPage(() => import('./commissions/CommissionsOtherStatementPage'), 'CommissionsOtherStatementPage');
const CommissionsOtherConfigPage = lazyPage(() => import('./commissions/CommissionsOtherConfigPage'), 'CommissionsOtherConfigPage');
const ConfigEmailSendersPage = lazyPage(() => import('./admin/config/ConfigEmailSendersPage'), 'ConfigEmailSendersPage');
const ConfigTilesPage = lazyPage(() => import('./admin/config/ConfigTilesPage'), 'ConfigTilesPage');
const AdminAuditPage = lazyPage(() => import('./admin/AdminAuditPage'), 'AdminAuditPage');
const AdminHubUsagePage = lazyPage(() => import('./admin/AdminHubUsagePage'), 'AdminHubUsagePage');
const FtePage = lazyPage(() => import('./admin/FtePage'), 'FtePage');
const AdminPerformanceReviewsPage = lazyPage(() => import('./admin/AdminPerformanceReviewsPage'), 'AdminPerformanceReviewsPage');
const PerformanceReviewsPage = lazyPage(() => import('./review/PerformanceReviewsPage'), 'PerformanceReviewsPage');
const ReviewFillPage = lazyPage(() => import('./review/ReviewFillPage'), 'ReviewFillPage');
const ReviewInsightsPage = lazyPage(() => import('./review/ReviewInsightsPage'), 'ReviewInsightsPage');
const ReviewResultsPage = lazyPage(() => import('./review/ReviewResultsPage'), 'ReviewResultsPage');
const ReviewTeamPage = lazyPage(() => import('./review/ReviewTeamPage'), 'ReviewTeamPage');
const ReviewCyclePage = lazyPage(() => import('./review/ReviewCyclePage'), 'ReviewCyclePage');
const PortfolioPage = lazyPage(() => import('./projects/PortfolioPage'), 'PortfolioPage');
const ProjectPage = lazyPage(() => import('./projects/ProjectPage'), 'ProjectPage');
const MyItemsPage = lazyPage(() => import('./projects/MyItemsPage'), 'MyItemsPage');
const RequestsPage = lazyPage(() => import('./projects/RequestsPage'), 'RequestsPage');
const TimelinePage = lazyPage(() => import('./projects/TimelinePage'), 'TimelinePage');
const NewsletterListPage = lazyPage(() => import('./newsletter/NewsletterListPage'), 'NewsletterListPage');
const OutreachComposePage = lazyPage(() => import('./outreach/OutreachComposePage'), 'OutreachComposePage');
const OutreachTemplatesPage = lazyPage(() => import('./outreach/OutreachTemplatesPage'), 'OutreachTemplatesPage');
const OutreachSentPage = lazyPage(() => import('./outreach/OutreachSentPage'), 'OutreachSentPage');
const OutreachCampaignsPage = lazyPage(() => import('./outreach/OutreachCampaignsPage'), 'OutreachCampaignsPage');
const BusinessCardPage = lazyPage(() => import('./businessCard/BusinessCardPage'), 'BusinessCardPage');
const OutreachCampaignDetailPage = lazyPage(() => import('./outreach/OutreachCampaignDetailPage'), 'OutreachCampaignDetailPage');
const NewsletterEditionsPage = lazyPage(() => import('./newsletter/NewsletterEditionsPage'), 'NewsletterEditionsPage');
const NewsletterIssuePage = lazyPage(() => import('./newsletter/NewsletterIssuePage'), 'NewsletterIssuePage');
const NewsletterSetupPage = lazyPage(() => import('./newsletter/NewsletterSetupPage'), 'NewsletterSetupPage');
const OverduePage = lazyPage(() => import('./projects/OverduePage'), 'OverduePage');
const ProjectsAdminPage = lazyPage(() => import('./projects/ProjectsAdminPage'), 'ProjectsAdminPage');

/**
 * Top-level shell + router.
 *
 * Routing strategy:
 *   - When auth or profile is loading → render a centered "Loading…" splash.
 *   - When signed out → render <LoginPage> (no header). Login URL is "/".
 *   - When signed in but no /users doc yet → "provisioning" (loading).
 *   - When inactive → <InactivePage>.
 *   - When profile incomplete → <ContactHrPage>.
 *   - Otherwise (status === 'ready') → render the app with its header,
 *     and use react-router for in-app routes.
 *
 * Route layout:
 *   /                      → HubLandingPage (the hub itself)
 *   /expenses              → DashboardPage (Expense Tool home)
 *   /expenses/statements/:id, /expenses/approvals → tool sub-routes
 *   /resources             → top-level (shared across tools eventually)
 *   /admin/*               → hub-level admin (users, config, etc.)
 *
 * Each tool route is wrapped in <RequireToolAccess slug="..."> which
 * redirects unprovisioned users back to the hub.
 */
export function App() {
  const { state: authState } = useAuth();
  const { state: profileState } = useProfile();

  // ---- Auth-level branches -----------------------------------------------

  if (authState.status === 'loading') return <LoadingSplash />;

  if (authState.status === 'signed_out' || authState.status === 'wrong_domain') {
    return <LoginPage />;
  }

  // signed_in — now branch on profile state.

  if (profileState.status === 'loading' || profileState.status === 'provisioning') {
    return <LoadingSplash />;
  }

  if (profileState.status === 'inactive') return <InactivePage />;
  if (profileState.status === 'incomplete') return <ContactHrPage />;

  // ---- Fully signed-in shell with router --------------------------------

  return (
    <AppLockGate>
      <BrowserRouter>
        {!isEmbedded() && <ApplyUpdateOnNavigate />}
        {/* Profile walk-through (Oct 2026): auto-opens on Home when switched on and something is missing. */}
        {!isEmbedded() && <ProfileOnboardingModal />}
        <TodosProvider>
        <Layout>
        <UsagePing />
        <Routes>
          {/* Hub landing (tiles) */}
          <Route path="/" element={<HubLandingPage />} />

          {/* Self-service profile — any signed-in user */}
          <Route path="/profile" element={<SelfProfilePage />} />
          <Route path="/my-team" element={<MyTeamPage />} />
          {/* Mobile tabs (Sep 2026): every app + the full to-do list. Work on desktop too. */}
          <Route path="/more" element={<MorePage />} />
          <Route path="/activity" element={<ActivityPage />} />
          {/* DAAily CRM test — separate app; this page links out (super admins). */}
          <Route path="/crm" element={<CrmLinkPage />} />
          {/* Hub Guide (Sep 2026): every signed-in user; docs filtered to their apps. */}
          <Route path="/guide" element={<GuidePage />} />
          {/* Board presentation — component self-gates to Michael. */}
          <Route path="/board" element={<BoardPage />} />
          <Route path="/verify-private-email" element={<VerifyPrivateEmailPage />} />

          {/* Receipt-request task — any signed-in user (no CC tool access needed) */}
          <Route path="/receipt-request/:requestId" element={<ReceiptRequestPage />} />

          {/* Share a credit card receipt into a colleague's library — any signed-in user */}
          <Route path="/share-receipt" element={<ShareReceiptPage />} />

          {/* Signatures — sidebar Personal (sign) | Team (send + track).
              SIGNING itself is open to any signed-in user via /sign/:id. */}
          <Route
            path="/signatures"
            element={
              <RequireToolAccess slug="signatures">
                <SignaturesPersonalPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/signatures/team"
            element={
              <RequireToolAccess slug="signatures">
                <SignaturesTeamPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/signatures/templates"
            element={
              <RequireToolAccess slug="signatures">
                <SignaturesTemplatesPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/signatures/stamps"
            element={
              <RequireToolAccess slug="signatures">
                <SignaturesStampsPage />
              </RequireToolAccess>
            }
          />
          <Route path="/sign/:requestId" element={<SignPage />} />

          {/* Onboarding — responsible-person to-dos + a checklist (server-gated) */}
          <Route path="/onboarding" element={<MyOnboardingPage />} />
          <Route path="/onboarding/checklists/:id" element={<OnboardingChecklistPage />} />

          {/* My feedback — any signed-in user */}
          <Route path="/feedback" element={<MyFeedbackPage />} />

          {/* ── Expense Tool — parent renders the tab sub-nav; children fill it ── */}
          <Route
            path="/expenses"
            element={
              <RequireToolAccess slug="expenses">
                <ExpensesSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="allowances" element={<AllowancesPage />} />
            <Route path="approvals" element={<AccountingDashboardPage />} />
            <Route path="team" element={<ExpenseTeamPage />} />
          </Route>
          {/* Statement detail — drill-in, no section tabs */}
          <Route
            path="/expenses/statements/:id"
            element={
              <RequireToolAccess slug="expenses">
                <StatementDetailPage />
              </RequireToolAccess>
            }
          />

          {/* ── Invoice Submission ── */}
          <Route
            path="/invoices"
            element={
              <RequireToolAccess slug="invoices">
                <InvoicesSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<InvoicesDashboardPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route
              path="approvals"
              element={
                <RequireInvoiceApprover>
                  <InvoiceApprovalsPage />
                </RequireInvoiceApprover>
              }
            />
          </Route>
          <Route
            path="/invoices/new"
            element={
              <RequireToolAccess slug="invoices">
                <InvoiceFormPage isNew={true} />
              </RequireToolAccess>
            }
          />
          <Route
            path="/invoices/approvals/:id"
            element={
              <RequireToolAccess slug="invoices">
                <RequireInvoiceApprover>
                  <InvoiceReviewPage />
                </RequireInvoiceApprover>
              </RequireToolAccess>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <RequireToolAccess slug="invoices">
                <InvoiceDetailPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/invoices/:id/edit"
            element={
              <RequireToolAccess slug="invoices">
                <InvoiceFormPage isNew={false} />
              </RequireToolAccess>
            }
          />

          {/* ── Credit Card Submissions ── */}
          <Route
            path="/credit-cards"
            element={
              <RequireToolAccess slug="creditCards">
                <CreditCardsSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<CreditCardDashboardPage />} />
            <Route path="library" element={<CreditCardLibraryPage />} />
            <Route path="approvals" element={<CreditCardReviewQueuePage />} />
          </Route>
          <Route
            path="/credit-cards/new"
            element={
              <RequireToolAccess slug="creditCards">
                <NewCreditCardStatementPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/credit-cards/statements/:id"
            element={
              <RequireToolAccess slug="creditCards">
                <CreditCardStatementDetailPage />
              </RequireToolAccess>
            }
          />

          {/* Absence Management — internal HR tool, provisioned users only */}
          <Route
            path="/absence"
            element={
              <RequireToolAccess slug="absence">
                <AbsenceSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<AbsenceDashboardPage />} />
            <Route path="calendar" element={<AbsenceCalendarPage />} />
            <Route path="team-calendar" element={<AbsenceCalendarPage />} />
            <Route path="member" element={<AbsenceMemberPage />} />
            <Route path="approvals" element={<AbsenceApprovalsPage />} />
            <Route path="summaries" element={<AbsenceSummariesPage />} />
            <Route path="warnings" element={<AbsenceWarningsPage />} />
            <Route path="carry-over" element={<AbsenceCarryOverPage />} />
            {/* Remote.com moved to Admin → Remote.com (Oct 2026); old links keep working. */}
            <Route path="remote" element={<Navigate to="/admin/remote" replace />} />
            <Route path="remote/documents" element={<Navigate to="/admin/remote/documents" replace />} />
          </Route>

          {/* DAAily Drivers — peer recognition, provisioned users only */}
          <Route
            path="/drivers"
            element={
              <RequireToolAccess slug="drivers">
                <DriversSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<DriversCabinetPage />} />
            <Route path="nominations" element={<DriversReceivedPage />} />
            <Route path="nominations/given" element={<DriversGivenPage />} />
            <Route path="team" element={<DriversTeamPage />} />
            <Route path="board" element={<DriversBoardPage />} />
            <Route path="summary" element={<DriversOrgSummaryPage />} />
          </Route>
          <Route
            path="/drivers/new"
            element={
              <RequireToolAccess slug="drivers">
                <NewNominationPage />
              </RequireToolAccess>
            }
          />

          {/* Projects — portfolio management, provisioned users only.
              Personal (/projects) = participating-only scope for everyone;
              Team (/projects/team) = the full portfolio, Projects admins only. */}
          <Route
            path="/projects"
            element={
              <RequireToolAccess slug="projects">
                <ProjectsSection scope="personal" />
              </RequireToolAccess>
            }
          >
            <Route index element={<PortfolioPage scope="personal" />} />
            <Route path="items" element={<MyItemsPage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="timeline" element={<TimelinePage scope="personal" />} />
          </Route>
          <Route
            path="/projects/team"
            element={
              <RequireToolAccess slug="projects">
                <RequireProjectsAdmin>
                  <ProjectsSection scope="team" />
                </RequireProjectsAdmin>
              </RequireToolAccess>
            }
          >
            <Route index element={<PortfolioPage scope="team" />} />
            <Route path="timeline" element={<TimelinePage scope="team" />} />
            <Route path="overdue" element={<OverduePage />} />
          </Route>
          <Route
            path="/projects/admin"
            element={
              <RequireToolAccess slug="projects">
                <RequireProjectsAdmin>
                  <ProjectsSection scope="admin" />
                </RequireProjectsAdmin>
              </RequireToolAccess>
            }
          >
            <Route index element={<ProjectsAdminPage />} />
          </Route>
          {/* Project detail — separate route (no tab bar) */}
          <Route
            path="/projects/:id"
            element={
              <RequireToolAccess slug="projects">
                <ProjectPage />
              </RequireToolAccess>
            }
          />

          {/* Outreach — templated tracked emails (internal, provisioned users only). */}
          <Route path="/outreach" element={<RequireToolAccess slug="outreach"><OutreachComposePage /></RequireToolAccess>} />
          <Route path="/outreach/templates" element={<RequireToolAccess slug="outreach"><OutreachTemplatesPage /></RequireToolAccess>} />
          <Route path="/outreach/sent" element={<RequireToolAccess slug="outreach"><OutreachSentPage /></RequireToolAccess>} />
          <Route path="/outreach/campaigns" element={<RequireToolAccess slug="outreach"><OutreachCampaignsPage /></RequireToolAccess>} />
          <Route path="/outreach/campaigns/:id" element={<RequireToolAccess slug="outreach"><OutreachCampaignDetailPage /></RequireToolAccess>} />
          <Route path="/business-card" element={<RequireToolAccess slug="businessCard"><BusinessCardPage /></RequireToolAccess>} />

          {/* Newsletters — internal, provisioned users only. */}
          <Route path="/newsletter" element={<RequireToolAccess slug="newsletter"><NewsletterListPage /></RequireToolAccess>} />
          <Route path="/newsletter/:id" element={<RequireToolAccess slug="newsletter"><NewsletterEditionsPage /></RequireToolAccess>} />
          <Route path="/newsletter/:id/edition/:editionId" element={<RequireToolAccess slug="newsletter"><NewsletterIssuePage /></RequireToolAccess>} />
          <Route path="/newsletter/:id/setup" element={<RequireToolAccess slug="newsletter"><NewsletterSetupPage /></RequireToolAccess>} />

          {/* Directors' Room — directors only (tools.directors.enabled), no admin override. */}
          {directorsRoutes}

          {/* Commissions — internal, provisioned users only. Config lives inside
              the module (admins only). */}
          <Route
            path="/commissions"
            element={
              <RequireToolAccess slug="commissions">
                <CommissionsSection />
              </RequireToolAccess>
            }
          >
            <Route index element={<CommissionsLandingPage />} />
            <Route path="statement" element={<CommissionsStatementPage />} />
            {/* Agents layer */}
            <Route path="agents/statement" element={<CommissionsAgentStatementPage />} />
            <Route
              path="agents"
              element={<RequireCommissionsAdmin><CommissionsAgentsPage /></RequireCommissionsAdmin>}
            />
            <Route
              path="agents/config"
              element={<RequireCommissionsAdmin><CommissionsAgentsConfigPage /></RequireCommissionsAdmin>}
            />
            <Route
              path="agents/payout"
              element={<RequireCommissionsAdmin><CommissionsAgentPayoutPage /></RequireCommissionsAdmin>}
            />
            {/* Other layer — person-specific bonus plans (quarterly). */}
            <Route path="other/statement" element={<CommissionsOtherStatementPage />} />
            <Route
              path="other"
              element={<RequireCommissionsAdmin><CommissionsOtherPage /></RequireCommissionsAdmin>}
            />
            <Route
              path="other/config"
              element={<RequireCommissionsAdmin><CommissionsOtherConfigPage /></RequireCommissionsAdmin>}
            />
            <Route
              path="payout"
              element={
                <RequireCommissionsAdmin>
                  <CommissionsPayoutPage />
                </RequireCommissionsAdmin>
              }
            />
            <Route
              path="accrual"
              element={
                <RequireCommissionsAdmin>
                  <CommissionsAccrualPage />
                </RequireCommissionsAdmin>
              }
            />
            <Route
              path="config"
              element={
                <RequireCommissionsAdmin>
                  <ConfigCommissionsPage />
                </RequireCommissionsAdmin>
              }
            />
          </Route>

          {/* USL — billed-revenue vs target dashboard (internal). */}
          <Route
            path="/usl"
            element={
              <RequireToolAccess slug="usl">
                <UslDashboardPage />
              </RequireToolAccess>
            }
          />
          <Route path="/usl/config" element={<RequireToolAccess slug="usl"><UslConfigPage /></RequireToolAccess>} />

          {/* Subscriptions — ArchDaily Stripe: Revenue | Health sub-tabs (internal). */}
          <Route
            path="/subscriptions"
            element={
              <RequireToolAccess slug="subscriptions">
                <SubscriptionsSection />
              </RequireToolAccess>
            }
          >
            {/* Self-registering — see web/src/subscriptions/registry.tsx. Each
                sub-nav is wrapped in a per-page access guard (deny-by-default). */}
            {SUBSCRIPTION_NAV.map((d) =>
              d.path === ''
                ? <Route key="idx" index element={<SubscriptionPageGuard entryKey={d.key} admin={d.admin} requiresAnalytics={d.requiresAnalytics}>{d.element}</SubscriptionPageGuard>} />
                : <Route key={d.path} path={d.path} element={<SubscriptionPageGuard entryKey={d.key} admin={d.admin} requiresAnalytics={d.requiresAnalytics}>{d.element}</SubscriptionPageGuard>} />,
            )}
          </Route>

          {/* Analytics — BigQuery traffic dashboards (internal). */}
          <Route
            path="/analytics"
            element={
              <RequireToolAccess slug="analytics">
                <AnalyticsSection />
              </RequireToolAccess>
            }
          >
            {/* Self-registering — see web/src/analytics/registry.tsx. Each page
                is wrapped in a per-page access guard (deny-by-default viewers). */}
            {ANALYTICS_DASHBOARDS.map((d) =>
              d.path === ''
                ? <Route key="idx" index element={<AnalyticsPageGuard entryKey={d.key} admin={d.admin}>{d.element}</AnalyticsPageGuard>} />
                : <Route key={d.path} path={d.path} element={<AnalyticsPageGuard entryKey={d.key} admin={d.admin}>{d.element}</AnalyticsPageGuard>} />,
            )}
          </Route>

          {/* Operations — DAAily Operations (internal). Self-registering — see
              web/src/operations/registry.tsx (same pattern as analytics). */}
          <Route
            path="/operations"
            element={
              <RequireToolAccess slug="operations">
                <OperationsSection />
              </RequireToolAccess>
            }
          >
            {OPERATIONS_DASHBOARDS.map((d) =>
              d.path === ''
                ? <Route key="idx" index element={<OperationsPageGuard entryKey={d.key} admin={d.admin}>{d.element}</OperationsPageGuard>} />
                : <Route key={d.path} path={d.path} element={<OperationsPageGuard entryKey={d.key} admin={d.admin}>{d.element}</OperationsPageGuard>} />,
            )}
          </Route>

          {/* Org Chart — internal HR tool, provisioned users only */}
          <Route
            path="/org"
            element={
              <RequireToolAccess slug="orgChart">
                <OrgChartPage />
              </RequireToolAccess>
            }
          />
          {/* Public profile — readable by any signed-in user (field-gated per
              the visibility policy); the chart/people list itself stays
              behind the orgChart tool guard above. */}
          <Route path="/org/people/:email" element={<OrgProfilePage />} />

          {/* Performance Reviews — provisioned only (auto-enabled when a review
              is assigned; hidden otherwise). */}
          <Route
            path="/performance-reviews"
            element={
              <RequireToolAccess slug="performanceReviews">
                <PerformanceReviewsPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/performance-reviews/insights"
            element={
              <RequireToolAccess slug="performanceReviews">
                <ReviewInsightsPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/performance-reviews/cycle/:cycleId"
            element={
              <RequireToolAccess slug="performanceReviews">
                <ReviewCyclePage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/performance-reviews/team"
            element={
              <RequireToolAccess slug="performanceReviews">
                <ReviewTeamPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/performance-reviews/results/:cycleId"
            element={
              <RequireToolAccess slug="performanceReviews">
                <ReviewResultsPage />
              </RequireToolAccess>
            }
          />
          <Route
            path="/performance-reviews/:id"
            element={
              <RequireToolAccess slug="performanceReviews">
                <ReviewFillPage />
              </RequireToolAccess>
            }
          />

          {/* Document Management — internal HR tool, provisioned users only */}
          <Route
            path="/documents"
            element={
              <RequireToolAccess slug="documents">
                <Outlet />
              </RequireToolAccess>
            }
          >
            <Route index element={<DocumentsPage />} />
            <Route path="team" element={<TeamDocumentsPage />} />
          </Route>

          {/* Document Vault — standalone admin page (HR setup side).
              Sidebar sub-nav: Employee (per-employee vault) | Company (policies). */}
          <Route
            path="/document-vault"
            element={
              <RequireAdmin>
                <AdminDocumentVaultPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/document-vault/company"
            element={
              <RequireAdmin>
                <AdminDocumentVaultPage view="company" />
              </RequireAdmin>
            }
          />

          {/* Time Tracking — internal tool, provisioned users only */}
          <Route
            path="/time"
            element={
              <RequireToolAccess slug="timeTracking">
                <TimeTrackingPage />
              </RequireToolAccess>
            }
          />
          <Route path="/time/team" element={<RequireToolAccess slug="timeTracking"><TimeTrackingPage /></RequireToolAccess>} />
          <Route path="/time/approvals" element={<RequireToolAccess slug="timeTracking"><TimeTrackingPage /></RequireToolAccess>} />
          <Route path="/time/summaries" element={<RequireToolAccess slug="timeTracking"><TimeSummariesPage /></RequireToolAccess>} />

          {/* User profile — drill-in (no admin tab bar), admin-only */}
          <Route
            path="/admin/users/:email"
            element={
              <RequireAdmin>
                <UserProfilePage />
              </RequireAdmin>
            }
          />

          {/* ── Admin (hub-level — keeps its own left sub-nav) ── */}
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="access" element={<AdminAccessPage />} />
            <Route path="countries" element={<AdminCountriesPage />} />
            <Route path="resources" element={<AdminResourcesPage />} />
            <Route path="mail" element={<AdminMailPage />} />
            <Route path="email-previews" element={<AdminEmailPreviewsPage />} />
            <Route path="data-completeness" element={<AdminDataCompletenessPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="usage" element={<AdminHubUsagePage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="command-center-feed" element={<AdminCommandCenterFeedPage />} />
            {/* Remote.com (employer of record): sync overview · expense statements · document imports. */}
            <Route path="remote" element={<AdminRemoteLayout />}>
              <Route index element={<RemoteOverviewPage />} />
              <Route path="expenses" element={<RemoteExpensesPage />} />
              <Route path="documents" element={<RemoteDocumentsPage />} />
            </Route>
          </Route>

          {/* Config — its own top-level area (sidebar item), not an Admin tab. */}
          <Route
            path="/admin/config"
            element={
              <RequireAdmin>
                <ConfigLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<ConfigLandingPage />} />
            <Route path="hub" element={<ConfigHubPage />} />
            <Route path="expenses" element={<ConfigExpensePage />} />
            <Route path="invoices" element={<ConfigInvoicePage />} />
            <Route path="credit-cards" element={<ConfigCreditCardPage />} />
            <Route path="absence" element={<ConfigAbsencePage />} />
            <Route path="holidays" element={<ConfigHolidaysPage />} />
            <Route path="documents" element={<ConfigDocumentVaultPage />} />
            <Route path="onboarding" element={<ConfigOnboardingPage />} />
            <Route path="drivers" element={<ConfigDriversPage />} />
            <Route path="mutations" element={<ConfigMutationsPage />} />
            <Route path="email" element={<ConfigEmailSendersPage />} />
            <Route element={<ConfigAccessProfilesLayout />}>
              <Route path="access" element={<ConfigAccessPage />} />
              <Route path="profile" element={<ConfigProfileFieldsPage />} />
            </Route>
            <Route path="tiles" element={<ConfigTilesPage />} />
          </Route>

          {/* Onboarding — HR/admin management (sidebar item below Config). */}
          <Route
            path="/admin/onboarding"
            element={
              <RequireAdmin>
                <AdminOnboardingPage />
              </RequireAdmin>
            }
          />

          {/* FTE report — own top-level area (sidebar item below Config). */}
          <Route
            path="/admin/fte"
            element={
              <RequireAdmin>
                <FtePage />
              </RequireAdmin>
            }
          />

          {/* Monthly Mutations — HR checklist (admin or hub-admin). */}
          <Route
            path="/admin/mutations"
            element={
              <RequireMutationsAccess>
                <MutationsPage />
              </RequireMutationsAccess>
            }
          />

          {/* Performance reviews — admin authoring (sidebar item below Config). */}
          <Route
            path="/admin/performance-reviews"
            element={
              <RequireAdmin>
                <AdminPerformanceReviewsPage />
              </RequireAdmin>
            }
          />

          {/* Backwards-compat redirects for old flat URLs */}
          <Route path="/resources" element={<Navigate to="/expenses/resources" replace />} />
          <Route path="/statements/:id" element={<Navigate to="/expenses" replace />} />
        </Routes>
      </Layout>
      </TodosProvider>
      </BrowserRouter>
    </AppLockGate>
  );
}

function LoadingSplash() {
  return (
    <main className="min-h-screen flex items-center justify-center">
      <Loading />
    </main>
  );
}
